import React, { memo } from 'react';
import {
  Box,
  Typography,
  Rating,
  Chip,
  Tooltip,
  LinearProgress,
  Grid,
  Paper,
} from '@mui/material';
import {
  Star as StarIcon,
  Comment as CommentIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * TemplateFeedbackDisplay Component
 *
 * Displays feedback summary and analytics for a template in a compact format.
 * Shows average rating, total feedback count, and rating distribution.
 */
const TemplateFeedbackDisplay = ({
  feedback,
  analytics,
  summary,
  loading = false,
  compact = true,
}) => {
  // Handle loading state
  if (loading) {
    return (
      <Box sx={{ p: 1 }}>
        <LinearProgress size="small" />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Loading feedback...
        </Typography>
      </Box>
    );
  }

  // Handle no feedback state - return null (don't show anything)
  if (!feedback || feedback.length === 0) {
    return null;
  }

  const averageRating = analytics?.average_rating || 0;
  const totalFeedback = analytics?.total_feedback || feedback.length;
  const ratingDistribution = analytics?.rating_distribution || {};
  const categoryScores = analytics?.category_scores || {};

  // Compact display for TemplateCard
  if (compact) {
    return (
      <Box sx={{ p: 1 }}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography variant="body2" fontWeight="medium">
            {averageRating.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({totalFeedback})
          </Typography>
        </Box>

        {Object.keys(ratingDistribution).length > 0 && (
          <Box display="flex" gap={0.5} alignItems="center">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = ratingDistribution[rating] || 0;
              const percentage =
                totalFeedback > 0 ? (count / totalFeedback) * 100 : 0;

              return (
                <Tooltip key={rating} title={`${count} ratings`}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor:
                        percentage > 0 ? 'warning.main' : 'grey.300',
                      opacity: percentage > 0 ? 1 : 0.3,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  // Full display for detailed view
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <CommentIcon sx={{ mr: 1 }} />
        Template Feedback
      </Typography>

      <Grid container spacing={2}>
        {/* Average Rating */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <StarIcon sx={{ color: 'warning.main' }} />
            <Typography variant="h6">{averageRating.toFixed(1)}</Typography>
            <Typography variant="body2" color="text.secondary">
              out of 5
            </Typography>
          </Box>
          <Rating value={averageRating} readOnly precision={0.1} size="small" />
        </Grid>

        {/* Total Feedback */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PeopleIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">{totalFeedback}</Typography>
            <Typography variant="body2" color="text.secondary">
              feedback
            </Typography>
          </Box>
        </Grid>

        {/* Rating Distribution */}
        {Object.keys(ratingDistribution).length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rating Distribution
            </Typography>
            <Box>
              {[5, 4, 3, 2, 1].map(rating => {
                const count = ratingDistribution[rating] || 0;
                const percentage =
                  totalFeedback > 0 ? (count / totalFeedback) * 100 : 0;

                return (
                  <Box
                    key={rating}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={0.5}
                  >
                    <Typography variant="caption" sx={{ minWidth: 20 }}>
                      {rating}★
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" sx={{ minWidth: 30 }}>
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        )}

        {/* Category Scores */}
        {Object.keys(categoryScores).length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Category Performance
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {Object.entries(categoryScores).map(([category, score]) => (
                <Chip
                  key={category}
                  label={`${category}: ${score.toFixed(1)}`}
                  size="small"
                  color={
                    score >= 4 ? 'success' : score >= 3 ? 'warning' : 'error'
                  }
                  variant="outlined"
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

TemplateFeedbackDisplay.propTypes = {
  feedback: PropTypes.array,
  analytics: PropTypes.object,
  summary: PropTypes.object,
  loading: PropTypes.bool,
  compact: PropTypes.bool,
};

export default memo(TemplateFeedbackDisplay);
